const INVALID_FONT_KEY_CHARS = /[.#$[\]/]/g;
const FONT_MIME_BY_EXTENSION = {
  ttf: "font/ttf",
  otf: "font/otf",
  woff: "font/woff",
  woff2: "font/woff2",
};
const WEIGHT_NAMES = {
  100: "Thin",
  200: "ExtraLight",
  300: "Light",
  400: "Regular",
  500: "Medium",
  600: "SemiBold",
  700: "Bold",
  800: "ExtraBold",
  900: "Black",
};
const loadedFontFaces = new Set();

export const ADD_FONT_VALUE = "__add_font__";
export const FONT_KIND = {
  STATIC: "static",
  VARIABLE: "variable",
};

export const normalizeFontName = ({ name } = {}) =>
  String(name || "")
    .replace(/\s+/g, " ")
    .trim();

export const toFontKey = ({ name } = {}) =>
  normalizeFontName({ name })
    .toLowerCase()
    .replace(INVALID_FONT_KEY_CHARS, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");

export const validateFontName = ({ name } = {}) => {
  const fontName = normalizeFontName({ name });
  if (!fontName) return "required";
  if (!toFontKey({ name: fontName })) return "invalidName";
  return null;
};

export const getFontMimeType = ({ file } = {}) => {
  const extension = String(file?.name || "")
    .split(".")
    .pop()
    ?.toLowerCase();
  return FONT_MIME_BY_EXTENSION[extension] || null;
};

export const normalizeFontWeight = (value) => {
  const weight = Number(value);
  if (!Number.isFinite(weight) || weight <= 0) return 400;
  return Math.max(1, Math.min(1000, Math.round(weight)));
};

export const toFaceKey = ({ weight, italic } = {}) => {
  const nextWeight = normalizeFontWeight(weight);
  return italic ? `${nextWeight}i` : String(nextWeight);
};

export const faceDisplayName = ({ weight, italic, name } = {}) => {
  const explicit = normalizeFontName({ name });
  if (explicit) return explicit;
  const rounded = Math.round(normalizeFontWeight(weight) / 100) * 100;
  const weightName = WEIGHT_NAMES[rounded] || String(normalizeFontWeight(weight));
  return italic ? `${weightName} Italic` : weightName;
};

const titleCaseSlug = (slug) =>
  String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const lookupName = (value) => normalizeFontName({ name: value });

const isFamilyRecord = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      (value.faces ||
        value.namedInstances ||
        value.kind === FONT_KIND.VARIABLE ||
        value.kind === FONT_KIND.STATIC)
  );

const sortFaces = (left, right) => {
  if (left.weight !== right.weight) return left.weight - right.weight;
  if (Boolean(left.italic) !== Boolean(right.italic)) {
    return left.italic ? 1 : -1;
  }
  return String(left.name).localeCompare(String(right.name), undefined, {
    sensitivity: "base",
  });
};

const createFace = ({
  key,
  name,
  url,
  mimeType = "",
  weight = 400,
  italic = false,
}) => {
  if (!url) return null;
  const nextWeight = normalizeFontWeight(weight);
  const nextItalic = Boolean(italic);
  const nextKey = key || toFaceKey({ weight: nextWeight, italic: nextItalic });
  return {
    key: nextKey,
    name: faceDisplayName({ weight: nextWeight, italic: nextItalic, name }),
    url,
    mimeType,
    weight: nextWeight,
    italic: nextItalic,
  };
};

const emptyFamily = ({
  key,
  familyName,
  kind = FONT_KIND.STATIC,
  url = "",
  mimeType = "",
  axes = null,
}) => ({
  key,
  familyName,
  kind: kind === FONT_KIND.VARIABLE ? FONT_KIND.VARIABLE : FONT_KIND.STATIC,
  faces: {},
  namedInstances: {},
  axes: axes || null,
  url: kind === FONT_KIND.VARIABLE ? url : "",
  mimeType,
});

export const mergeFamilies = (current, incoming) => {
  if (!incoming) return current || null;
  if (!current) return incoming;
  if (current.kind !== incoming.kind) return incoming;
  return {
    ...current,
    ...incoming,
    faces: { ...(current.faces || {}), ...(incoming.faces || {}) },
    namedInstances: {
      ...(current.namedInstances || {}),
      ...(incoming.namedInstances || {}),
    },
    axes: incoming.axes || current.axes || null,
    url: incoming.url || current.url || "",
    mimeType: incoming.mimeType || current.mimeType || "",
    creationTime: current.creationTime || incoming.creationTime,
    updateTime: incoming.updateTime || current.updateTime,
  };
};

const ingestFaces = ({ facesNode, fallbackUrl, fallbackMimeType }) => {
  const faces = {};
  Object.entries(facesNode || {}).forEach(([faceKey, face]) => {
    if (!face || typeof face !== "object") return;
    const next = createFace({
      key: face.key || faceKey,
      name: face.name,
      url: face.url || fallbackUrl,
      mimeType: face.mimeType || fallbackMimeType,
      weight: face.weight,
      italic: face.italic,
    });
    if (next) faces[next.key] = next;
  });
  return faces;
};

const ingestNamedInstances = (instancesNode) => {
  const namedInstances = {};
  Object.entries(instancesNode || {}).forEach(([instanceKey, instance]) => {
    if (!instance || typeof instance !== "object") return;
    const name = normalizeFontName({ name: instance.name }) || instanceKey;
    const weight = normalizeFontWeight(instance.weight);
    const italic = Boolean(instance.italic);
    const key =
      instance.key || instanceKey || toFontKey({ name }) || toFaceKey({ weight, italic });
    namedInstances[key] = {
      key,
      name,
      weight,
      italic,
    };
  });
  return namedInstances;
};

const ingestFamilyRecord = (key, value) => {
  const familyName = lookupName(value.familyName || value.name) || titleCaseSlug(key);
  const familyKey = value.key || toFontKey({ name: familyName }) || key;
  const kind =
    value.kind === FONT_KIND.VARIABLE ? FONT_KIND.VARIABLE : FONT_KIND.STATIC;
  const faces = ingestFaces({
    facesNode: value.faces,
    fallbackUrl: value.url,
    fallbackMimeType: value.mimeType,
  });
  if (!Object.keys(faces).length && typeof value.url === "string" && value.url) {
    const weight = normalizeFontWeight(value.weight);
    const italic = Boolean(value.italic);
    const face = createFace({
      key: toFaceKey({ weight, italic }),
      name: value.subfamily || "Regular",
      url: value.url,
      mimeType: value.mimeType,
      weight,
      italic,
    });
    if (face) faces[face.key] = face;
  }
  const family = emptyFamily({
    key: familyKey,
    familyName,
    kind,
    url: value.url || Object.values(faces)[0]?.url || "",
    mimeType: value.mimeType || Object.values(faces)[0]?.mimeType || "",
    axes: value.axes || null,
  });
  family.faces = faces;
  family.namedInstances = ingestNamedInstances(value.namedInstances);
  family.creationTime = value.creationTime;
  family.updateTime = value.updateTime;
  return family;
};

const ingestLegacyFlat = (key, value) => {
  const familyName = lookupName(value.name) || titleCaseSlug(key) || key;
  const familyKey = value.key || toFontKey({ name: familyName }) || key;
  const face = createFace({
    key: toFaceKey({ weight: 400, italic: false }),
    name: "Regular",
    url: value.url,
    mimeType: value.mimeType,
    weight: 400,
    italic: false,
  });
  const family = emptyFamily({
    key: familyKey,
    familyName,
    kind: FONT_KIND.STATIC,
    mimeType: value.mimeType || "",
  });
  if (face) family.faces[face.key] = face;
  family.creationTime = value.creationTime;
  family.updateTime = value.updateTime;
  return family;
};

const ingestLegacyRegularBold = (key, value) => {
  const familyName = titleCaseSlug(key) || key;
  const familyKey = toFontKey({ name: familyName }) || key;
  const family = emptyFamily({
    key: familyKey,
    familyName,
    kind: FONT_KIND.STATIC,
  });
  if (typeof value.regular === "string" && value.regular) {
    const face = createFace({
      key: toFaceKey({ weight: 400, italic: false }),
      name: "Regular",
      url: value.regular,
      weight: 400,
      italic: false,
    });
    if (face) family.faces[face.key] = face;
  }
  if (typeof value.bold === "string" && value.bold) {
    const face = createFace({
      key: toFaceKey({ weight: 700, italic: false }),
      name: "Bold",
      url: value.bold,
      weight: 700,
      italic: false,
    });
    if (face) family.faces[face.key] = face;
  }
  return Object.keys(family.faces).length ? family : null;
};

export const flattenFontsNode = (node = {}) => {
  const families = {};

  Object.entries(node || {}).forEach(([key, value]) => {
    if (!value || typeof value !== "object") return;
    let family = null;
    if (isFamilyRecord(value)) {
      family = ingestFamilyRecord(key, value);
    } else if (typeof value.url === "string" && value.url) {
      family = ingestLegacyFlat(key, value);
    } else {
      family = ingestLegacyRegularBold(key, value);
    }
    if (!family?.key) return;
    families[family.key] = mergeFamilies(families[family.key], family);
  });

  return families;
};

export const listSelectableFaces = (family) => {
  if (!family) return [];
  const fileUrl =
    family.url || Object.values(family.faces || {})[0]?.url || "";
  const mimeType =
    family.mimeType || Object.values(family.faces || {})[0]?.mimeType || "";
  if (family.kind === FONT_KIND.VARIABLE) {
    const instances = Object.values(family.namedInstances || {});
    if (instances.length) {
      return instances
        .map((instance) => ({
          ...instance,
          url: instance.url || fileUrl,
          mimeType: instance.mimeType || mimeType,
        }))
        .filter((face) => face.url)
        .sort(sortFaces);
    }
  }
  return Object.values(family.faces || {}).sort(sortFaces);
};

export const getDefaultFace = (family) => {
  const faces = listSelectableFaces(family);
  if (!faces.length) return null;
  return (
    faces.find((face) => face.weight === 400 && !face.italic) ||
    faces.find((face) => /regular/i.test(face.name) && !face.italic) ||
    faces[0]
  );
};

export const faceCatalogName = (family, face) => {
  const familyName = family?.familyName || "";
  const faceName = face?.name || "";
  const faces = listSelectableFaces(family);
  if (!familyName) return faceName;
  if (faces.length <= 1) return familyName;
  if (!faceName || faceName.toLowerCase() === familyName.toLowerCase()) {
    return familyName;
  }
  return `${familyName} ${faceName}`;
};

export const toFacesByName = (families = {}) => {
  const facesByName = {};
  Object.values(families).forEach((family) => {
    listSelectableFaces(family).forEach((face) => {
      const name = faceCatalogName(family, face);
      facesByName[name] = {
        name,
        url: face.url,
        mimeType: face.mimeType || family.mimeType || "",
        key: family.key,
        familyName: family.familyName,
        faceKey: face.key,
        weight: face.weight,
        italic: face.italic,
        kind: family.kind,
      };
    });
  });
  return facesByName;
};

export const mergeFontCatalogs = ({
  commonFonts = {},
  advertiserFonts = {},
} = {}) => {
  const merged = { ...flattenFontsNode(commonFonts) };
  Object.entries(flattenFontsNode(advertiserFonts)).forEach(([key, family]) => {
    merged[key] = mergeFamilies(merged[key], family);
  });
  return Object.values(merged).sort((left, right) =>
    left.familyName.localeCompare(right.familyName, undefined, {
      sensitivity: "base",
    })
  );
};

const asFamilyList = (families) =>
  Array.isArray(families) ? families : Object.values(families || {});

export const findFamily = ({ families = [], fontAssetRef } = {}) => {
  const wanted = lookupName(fontAssetRef);
  if (!wanted) return null;
  const wantedKey = toFontKey({ name: wanted });
  const list = asFamilyList(families);
  return (
    list.find(
      (family) =>
        lookupName(family.familyName) === wanted ||
        family.key === wantedKey ||
        toFontKey({ name: family.familyName }) === wantedKey
    ) || null
  );
};

const findFaceInFamily = ({ family, fontAssetRef, fontFaceRef }) => {
  const faces = listSelectableFaces(family);
  if (!faces.length) return null;
  if (fontFaceRef) {
    const wantedFace = lookupName(fontFaceRef);
    const wantedKey = toFontKey({ name: fontFaceRef });
    const match = faces.find(
      (face) =>
        face.key === fontFaceRef ||
        lookupName(face.name) === wantedFace ||
        toFontKey({ name: face.name }) === wantedKey
    );
    if (match) return match;
  }
  const wanted = lookupName(fontAssetRef);
  if (wanted) {
    const wantedKey = toFontKey({ name: wanted });
    const byCatalogName = faces.find((face) => {
      const catalogName = faceCatalogName(family, face);
      return (
        lookupName(catalogName) === wanted ||
        toFontKey({ name: catalogName }) === wantedKey
      );
    });
    if (byCatalogName) return byCatalogName;
  }
  return getDefaultFace(family);
};

export const resolveLayerFont = ({
  families = [],
  fontAssetRef,
  fontFaceRef,
  fontWeight,
} = {}) => {
  const list = asFamilyList(families);
  if (!list.length) return null;
  const wanted = lookupName(fontAssetRef);
  let family = findFamily({ families: list, fontAssetRef });
  let face = family
    ? findFaceInFamily({ family, fontAssetRef, fontFaceRef })
    : null;

  if (!family && wanted) {
    const wantedKey = toFontKey({ name: wanted });
    list.some((candidate) => {
      const match = listSelectableFaces(candidate).find((item) => {
        const catalogName = faceCatalogName(candidate, item);
        return (
          lookupName(catalogName) === wanted ||
          toFontKey({ name: catalogName }) === wantedKey ||
          lookupName(item.name) === wanted
        );
      });
      if (!match) return false;
      family = candidate;
      face = fontFaceRef
        ? findFaceInFamily({ family: candidate, fontAssetRef, fontFaceRef }) ||
          match
        : match;
      return true;
    });
  }

  if (!family || !face) return null;
  const styleWeight = Number(fontWeight);
  return {
    familyName: family.familyName,
    familyKey: family.key,
    kind: family.kind,
    faceKey: face.key,
    name: faceCatalogName(family, face),
    url: face.url,
    mimeType: face.mimeType || family.mimeType || "",
    weight: normalizeFontWeight(
      Number.isFinite(styleWeight) && styleWeight > 0
        ? styleWeight
        : face.weight
    ),
    italic: Boolean(face.italic),
    produceFamilyName:
      family.kind === FONT_KIND.VARIABLE
        ? family.familyName
        : `${family.familyName}__${face.key}`,
  };
};

export const buildFontSelection = ({ family, face } = {}) => {
  const nextFace = face || getDefaultFace(family);
  if (!family || !nextFace) {
    return {
      fontAssetRef: family?.familyName || "",
      fontFaceRef: "",
      fontWeight: 400,
    };
  }
  return {
    fontAssetRef: family.familyName,
    fontFaceRef: nextFace.key,
    fontWeight: nextFace.weight,
  };
};

export const buildFaceFromUpload = ({ parsed = {}, url, mimeType = "" } = {}) =>
  createFace({
    key: parsed.faceKey,
    name: parsed.faceName || parsed.subfamily,
    url,
    mimeType,
    weight: parsed.weight,
    italic: parsed.italic,
  });

export const sanitizeFamilyForRtdb = (family) => {
  if (!family) return null;
  const faces = {};
  Object.entries(family.faces || {}).forEach(([key, face]) => {
    if (!face?.url) return;
    faces[key] = {
      key: face.key,
      name: face.name,
      url: face.url,
      mimeType: face.mimeType || "",
      weight: normalizeFontWeight(face.weight),
      italic: Boolean(face.italic),
    };
  });
  const payload = {
    key: family.key,
    familyName: family.familyName,
    kind: family.kind,
    faces,
    creationTime: family.creationTime,
    updateTime: family.updateTime,
  };
  if (family.kind === FONT_KIND.VARIABLE) {
    payload.url = family.url || Object.values(faces)[0]?.url || "";
    payload.mimeType = family.mimeType || Object.values(faces)[0]?.mimeType || "";
    payload.namedInstances = family.namedInstances || {};
    if (family.axes) payload.axes = family.axes;
  }
  return payload;
};

export const findMatchingFamily = ({ families = [], familyName, kind } = {}) => {
  const family = findFamily({ families, fontAssetRef: familyName });
  if (!family) return null;
  if (kind && family.kind !== kind) return null;
  return family;
};

export const toFamilyRecordForSave = ({
  existing,
  familyName,
  familyKey,
  kind,
  face,
  namedInstances,
  axes,
  url,
  mimeType,
  now,
}) => {
  const existingFamily = existing
    ? isFamilyRecord(existing)
      ? ingestFamilyRecord(familyKey, existing)
      : existing.url
        ? ingestLegacyFlat(familyKey, existing)
        : null
    : null;
  const nextKind =
    kind === FONT_KIND.VARIABLE ? FONT_KIND.VARIABLE : FONT_KIND.STATIC;
  if (existingFamily && existingFamily.kind !== nextKind) {
    return { conflict: true, existingKind: existingFamily.kind };
  }
  const family = mergeFamilies(
    existingFamily,
    emptyFamily({
      key: familyKey,
      familyName,
      kind: nextKind,
      url: nextKind === FONT_KIND.VARIABLE ? url : "",
      mimeType,
      axes,
    })
  );
  if (face) {
    family.faces = {
      ...(family.faces || {}),
      [face.key]: face,
    };
  }
  if (nextKind === FONT_KIND.VARIABLE) {
    family.url = url || family.url;
    family.mimeType = mimeType || family.mimeType;
    family.namedInstances = {
      ...(family.namedInstances || {}),
      ...(namedInstances || {}),
    };
    family.axes = axes || family.axes;
  }
  family.familyName = familyName;
  family.key = familyKey;
  family.kind = nextKind;
  family.creationTime = family.creationTime || now;
  family.updateTime = now;
  return { family, conflict: false };
};

const axisWeightRange = (family) => {
  const axis = family?.axes?.wght;
  if (!axis) return null;
  const min = Number(axis.min);
  const max = Number(axis.max);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
};

const registerFontFace = async ({
  familyName,
  url,
  weight,
  italic,
  axisRange,
}) => {
  if (!familyName || !url) return;
  const style = italic ? "italic" : "normal";
  const descriptors = axisRange
    ? { weight: `${axisRange.min} ${axisRange.max}`, style: "normal" }
    : { weight: String(normalizeFontWeight(weight)), style };
  const loadKey = `${familyName}::${url}::${descriptors.weight}::${descriptors.style}`;
  if (loadedFontFaces.has(loadKey)) return;
  try {
    const fontFace = new FontFace(
      familyName,
      `url(${JSON.stringify(url)})`,
      descriptors
    );
    await fontFace.load();
    document.fonts.add(fontFace);
    loadedFontFaces.add(loadKey);
  } catch (_error) {
    // Preview load is best-effort; the stored name remains the ref.
  }
};

export const loadFontFaces = async ({ fonts = [], families = fonts } = {}) => {
  const familyList = asFamilyList(families).filter((item) => item?.familyName);
  if (familyList.length) {
    await Promise.all(
      familyList.map(async (family) => {
        const axisRange =
          family.kind === FONT_KIND.VARIABLE ? axisWeightRange(family) : null;
        const faces = listSelectableFaces(family);
        const uniqueUrls = [
          ...new Map(
            faces
              .filter((face) => face.url)
              .map((face) => [face.url, face])
          ).values(),
        ];
        await Promise.all(
          uniqueUrls.map((face) =>
            registerFontFace({
              familyName: family.familyName,
              url: face.url,
              weight: face.weight,
              italic: face.italic,
              axisRange,
            })
          )
        );
        await Promise.all(
          faces.map((face) =>
            registerFontFace({
              familyName: faceCatalogName(family, face),
              url: face.url,
              weight: face.weight,
              italic: face.italic,
              axisRange: null,
            })
          )
        );
      })
    );
    return;
  }

  await Promise.all(
    (Array.isArray(fonts) ? fonts : []).map(async (font) => {
      if (!font?.name || !font?.url) return;
      await registerFontFace({
        familyName: font.name,
        url: font.url,
        weight: font.weight || 400,
        italic: Boolean(font.italic),
      });
    })
  );
};
